import { useMemo, useState } from "react";
import { useGetList } from "ra-core";
import { Tag } from "@carbon/react";
import { Add, Search } from "@carbon/icons-react";
import { Link } from "react-router";
import { List } from "@/components/admin/list";
import { TopToolbar } from "../layout/TopToolbar";

type Lead = {
  id: string;
  first_name: string;
  last_name: string;
  company_name: string;
  title?: string;
  email?: string | null;
  phone?: string | null;
  source: string;
  status: string;
  rating: string;
  created_at: string;
};

const statusLabels: Record<string, string> = {
  new: "新建", working: "跟进中", qualified: "已确认资格",
  nurture: "持续培育", converted: "已转化", disqualified: "不合格",
};
const sourceLabels: Record<string, string> = {
  website: "官网", referral: "客户推荐", event: "活动", outbound: "主动开发",
  partner: "合作伙伴", social: "社交媒体", other: "其他",
};

export const LeadList = () => {
  const [search, setSearch] = useState("");
  const { data = [], isPending, error } = useGetList<Lead>("leads", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "created_at", order: "DESC" },
  });
  const leads = useMemo(() => data.filter((lead) => {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    return [lead.first_name, lead.last_name, lead.company_name, lead.email, lead.phone]
      .some((value) => String(value ?? "").toLowerCase().includes(term));
  }), [data, search]);

  return (
    <List title={false} actions={<TopToolbar><Link to="/leads/create" className="cds--btn cds--btn--primary"><Add size={16} aria-hidden="true" />新建线索</Link></TopToolbar>}>
      <section className="orbitas-leads-page">
        <div className="orbitas-page-heading">
          <div><p className="orbitas-eyebrow">客户与市场</p><h1>线索</h1><p>统一管理潜在客户、来源、资格状态与销售跟进。</p></div>
          <div className="orbitas-lead-count"><strong>{leads.length}</strong><span>条线索</span></div>
        </div>
        <label className="orbitas-lead-search"><Search size={18} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索姓名、企业、邮箱或电话" /></label>
        {error && <div role="alert" className="orbitas-lead-alert">线索加载失败：{error instanceof Error ? error.message : "请稍后重试"}</div>}
        {isPending ? <div className="orbitas-lead-empty">正在加载线索…</div> : leads.length === 0 ? (
          <div className="orbitas-lead-empty"><h2>{search ? "没有匹配的线索" : "还没有线索"}</h2><p>{search ? "请尝试其他关键词。" : "创建第一条线索，开始记录潜在客户并推进资格审查。"}</p>{!search && <Link to="/leads/create" className="cds--btn cds--btn--primary"><Add size={16} aria-hidden="true" />创建第一条线索</Link>}</div>
        ) : (
          <div className="orbitas-lead-table-wrap"><table className="orbitas-lead-table"><thead><tr><th>姓名</th><th>企业</th><th>职位</th><th>邮箱</th><th>来源</th><th>状态</th><th>评分</th><th>创建时间</th></tr></thead>
            <tbody>{leads.map((lead) => <tr key={lead.id}>
              <td><Link to={`/leads/${lead.id}/show`} className="orbitas-lead-primary">{[lead.first_name, lead.last_name].filter(Boolean).join(" ") || "未命名线索"}</Link></td>
              <td>{lead.company_name || "—"}</td><td>{lead.title || "—"}</td><td>{lead.email || "—"}</td>
              <td>{sourceLabels[lead.source] ?? lead.source}</td><td><Tag type={lead.status === "qualified" ? "green" : lead.status === "disqualified" ? "red" : lead.status === "converted" ? "blue" : "cool-gray"}>{statusLabels[lead.status] ?? lead.status}</Tag></td>
              <td>{lead.rating === "hot" ? "高" : lead.rating === "cold" ? "低" : "中"}</td>
              <td>{lead.created_at ? new Date(lead.created_at).toLocaleDateString("zh-CN") : "—"}</td>
            </tr>)}</tbody></table></div>
        )}
      </section>
    </List>
  );
};
